import React, { useState } from 'react';
import { Alert, Stack, TextField } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { editAssemblyGroup } from '../../actions/assemblyGroupActions';
import { useAppDispatch, useAppSelector } from '../hooks';
import { EditAssemblyGroupPropertiesDialogProps } from './dialogs';

export function EditAssemblyGroupPropertiesDialog({ assemblyGroupGuid }: EditAssemblyGroupPropertiesDialogProps) {
  const dispatch = useAppDispatch();
  const assemblyGroups = useAppSelector((state) => state.assemblyGroups.data);
  const otherAssemblyGroups = Object.values(assemblyGroups).filter(
    (assemblyGroup) => assemblyGroup.guid !== assemblyGroupGuid,
  );

  const [name, setName] = useState<string>(assemblyGroups[assemblyGroupGuid].name);
  const [notes, setNotes] = useState<string | null>(assemblyGroups[assemblyGroupGuid].notes);
  const [didFail, setDidFail] = useState(false);
  const nameProblem = otherAssemblyGroups.some((existingGroup) => existingGroup.name === name.trim())
    ? 'Assembly Group with same name already exists'
    : null;

  async function onSubmit() {
    setDidFail(false);

    try {
      await dispatch(editAssemblyGroup(assemblyGroupGuid, { name, notes: notes === '' ? null : notes }));
    } catch (error) {
      setDidFail(true);

      console.error(error);

      return;
    }

    dispatch(closeCurrentPopup());
  }

  return (
    <Dialog
      title="Edit Assembly Group Properties"
      submitText="Edit"
      onSubmit={onSubmit}
      onCancel="close"
      enterKeySubmits
      submitDisabled={!!nameProblem}
    >
      <Stack spacing={2} sx={{ marginTop: 1 }}>
        {didFail && (
          <Alert variant="outlined" severity="error">
            Unable to edit assembly group.
          </Alert>
        )}

        <TextField
          autoFocus
          autoComplete="off"
          size="small"
          label="Name"
          sx={{ marginTop: 1, width: '400px' }}
          value={name}
          error={!!nameProblem}
          onFocus={(event) => event.target.select()}
          onChange={(event) => void setName(event.target.value)}
        />

        {nameProblem && (
          <Alert variant="outlined" severity="error">
            {nameProblem}
          </Alert>
        )}

        <TextField
          autoFocus
          autoComplete="off"
          size="small"
          multiline
          label="Notes"
          sx={{ marginTop: 1, width: '400px' }}
          value={notes ?? ''}
          onFocus={(event) => event.target.select()}
          onChange={(event) => void setNotes(event.target.value)}
        />
      </Stack>
    </Dialog>
  );
}
